'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { CatTextLink } from 'blocks-nextjs/blocks-react/components/TextLink';
import { DealerLocatorTermsCheckbox } from '@cat-ecom/pcc-components';
import useGetDealerInfo from 'components/dealer-locator/hooks/use-get-dealer-info';
import useReplaceTokensWithElements from 'hooks/use-replace-tokens-with-elements';
import { TERMS_LINKS } from 'constants/links';
import { getCatOrDealerLegalURL } from 'utils/url';

interface DealerLocatorTermsWrapperProps {
  dealerName: string;
  isDisabled?: boolean;
  storeId: string;
}

export const DealerLocatorTermsWrapper = ({
  dealerName,
  isDisabled,
  storeId
}: DealerLocatorTermsWrapperProps) => {
  const { lang }: esiteParams = useParams();
  const t = useTranslations();
  const replaceTokens = useReplaceTokensWithElements();
  const { getDealerInfo, data, isError, isLoading } = useGetDealerInfo();

  const termsOfUseKey = 'DEALERS_TERMS_OF_USE';
  const privacyKey = 'UR_CAT_DEALER_PRIVACY_NOTICE_HREF';
  const areLinksDisabled = isError || isLoading || !data?.seoURL;

  useEffect(() => {
    getDealerInfo(storeId);
  }, [getDealerInfo, storeId]);

  const getDealerName = () => <span data-testid="dealer">{dealerName}</span>;

  const termLinksValues = [
    <CatTextLink
      href={
        !areLinksDisabled
          ? getCatOrDealerLegalURL(
              TERMS_LINKS.CAT_LEGAL_TERMS_OF_USE,
              lang,
              true,
              ''
            )
          : undefined
      }
      key="UR_PARTS_TERMS_OF_USE_HREF"
      rel="noreferrer"
      size="sm"
      target="_blank"
    >
      {t('UR_PARTS_TERMS_OF_USE_HREF')}
    </CatTextLink>,
    <CatTextLink
      href={
        !areLinksDisabled
          ? getCatOrDealerLegalURL(
              TERMS_LINKS.CAT_PRIVACY_NOTICE,
              lang,
              true,
              ''
            )
          : undefined
      }
      key="UR_CAT_PRIVACY_NOTICE_HREF"
      rel="noreferrer"
      size="sm"
      target="_blank"
    >
      {t('UR_CAT_PRIVACY_NOTICE_HREF')}
    </CatTextLink>,
    <CatTextLink
      href={
        !areLinksDisabled
          ? getCatOrDealerLegalURL(
              TERMS_LINKS.CAT_TERMS_OF_USE,
              lang,
              false,
              data.seoURL
            )
          : undefined
      }
      key={termsOfUseKey}
      rel="noreferrer"
      size="sm"
      target="_blank"
    >
      <span>
        {replaceTokens(`${t(termsOfUseKey, { 0: '{0}' })} `, [getDealerName()])}
      </span>
    </CatTextLink>,
    <CatTextLink
      href={
        !areLinksDisabled
          ? getCatOrDealerLegalURL(
              TERMS_LINKS.DEALER_PRIVACY_POLICY,
              lang,
              false,
              data?.seoURL
            )
          : undefined
      }
      key={privacyKey}
      rel="noreferrer"
      size="sm"
      target="_blank"
    >
      <span>
        {replaceTokens(`${t(privacyKey, { 0: '{0}' })} `, [getDealerName()])}
      </span>
    </CatTextLink>
  ];
  // Replace tokens in translation with more tokens, to be replaced with Elements, since translation function demands values for tokens in translation but does not accept Elements
  const termLinks = replaceTokens(
    t('UR_CAT_ACCEPT_TERMS', { 0: '{0}', 1: '{1}', 2: '{2}', 3: '{3}' }),
    termLinksValues
  );

  return (
    <DealerLocatorTermsCheckbox
      title={t('1628_ACCEPT_TERMS_AND_CONDITIONS_2').replace('.', '')}
      label={` (${t('REQUIRED').toLowerCase()})`}
      isDisabled={isDisabled}
    >
      {termLinks}
    </DealerLocatorTermsCheckbox>
  );
};
